import SEOHelmet from '@/components/SEOHelmet';
import styles from './library.module.scss';
import useSession from '@/hooks/useSession';
import List from '@/components/List';
import { FormEvent, useState } from 'react';
import { WorkspaceFile } from '@/models/file';
import { LibraryService } from '@/services/library.service';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import Modal from '@/components/Modal/modal';
import Form from '@/components/Form';
import InputWrapper from '@/components/Form/InputWrapper';
import Button from '@/components/Button';
import UploadIcon from '@/assets/icons/upload.svg?react';
import { getWorkspaceFileURL, parseWorkspaceFile } from '@/utils/files';
import { Role } from '@/models/user';
import LoadingSpinner from '@/components/LoadingSpinner';
import useRecentlyUploadedFiles from './hooks/useRecentlyUploadedFiles';
import useCurrentDoc from './hooks/useCurrentDoc';
import { useQuery } from '@tanstack/react-query';
import SharepointPanel from './components/SharepointPanel';
import DocumentView from './components/DocumentView';

const Library = () => {
  const { session, orgIndex, setSession } = useSession();
  const { roles, organizations } = session;

  const { data: fetchedFiles = [], isFetching } = useQuery({
    queryKey: ['files', session.fileCount, orgIndex],
    queryFn: async () => {
      const currentOrgID = roles.includes(Role.SUPER_DATA_ADMIN)
        ? organizations[orgIndex].id
        : undefined;
      const allFiles = await LibraryService.getAllFiles(currentOrgID);

      setSession((prevSession) => ({
        ...prevSession,
        fileCount: allFiles.length,
      }));
      setFiles(allFiles);

      return allFiles;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const [files, setFiles] = useState<WorkspaceFile[]>(fetchedFiles);

  const [processingDoc, setProcessingDoc] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const currentOrgID = roles.includes(Role.SUPER_DATA_ADMIN)
    ? organizations[orgIndex].id
    : undefined;
  const { recentlyUploadedFiles, setRecentlyUploadedFiles } =
    useRecentlyUploadedFiles(currentOrgID || '', setFiles);
  const {
    docContentRef,
    currentDoc,
    setCurrentDoc,
    showContentsPreview,
    isLoading,
  } = useCurrentDoc(currentOrgID || '', files, setFiles);

  const handleUploadSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setProcessingDoc(true);
    const formData = new FormData(event.currentTarget);
    const file = formData.get('file') as File;
    const fileName = file.name;

    if (!fileName || !file) {
      toast.error('An error happened with the file uploaded.');
      setProcessingDoc(false);
      return;
    }

    try {
      const uploadResponse = await LibraryService.uploadFile(
        fileName,
        file,
        currentOrgID
      );
      const uploadedFile: WorkspaceFile = parseWorkspaceFile({
        ...uploadResponse,
        organization_id: currentOrgID || '',
        file_size: file.size,
        file_type: file.type,
      });

      if (!uploadResponse) {
        throw new Error('Failed to upload and ingest the document.');
      }

      setRecentlyUploadedFiles((prev) => {
        return [...prev, uploadedFile];
      });
      setShowUploadModal(false);
      setProcessingDoc(false);
      toast.info(`Document "${fileName}" is getting processed.`);
    } catch (err) {
      const error = err as Error;
      toast.error(
        error.message || 'An error occurred while uploading the document.'
      );
      setProcessingDoc(false);
    }
  };

  const enableFileManagement = roles.some((role) =>
    [Role.ORG_DATA_ADMIN, Role.SUPER_DATA_ADMIN].includes(role)
  );

  return (
    <div className={styles['document']}>
      <SEOHelmet title={currentDoc ? currentDoc.name : 'Document Library'} />
      <h1>Library</h1>

      {isFetching ? (
        <div className={styles['library__loading']}>
          <LoadingSpinner />
          <p>Updating document list...</p>
        </div>
      ) : (
        <>
          {enableFileManagement && (
            <div className={styles['library__actions']}>
              <Button
                variation="secondary"
                className={styles['upload-btn']}
                onClick={() => setShowUploadModal(true)}
              >
                <UploadIcon />
                Upload
              </Button>

              <SharepointPanel orgID={currentOrgID} />
            </div>
          )}

          <section className={styles['library__section']}>
            <DocumentView
              setCurrentDoc={setCurrentDoc}
              currentDoc={currentDoc}
              currentOrgID={currentOrgID}
              isLoading={isLoading}
              showContentsPreview={showContentsPreview}
              docContentRef={docContentRef}
              enableFileManagement={enableFileManagement}
            />

            <aside>
              <List
                items={[
                  ...recentlyUploadedFiles.map((file) => ({
                    title: file.name,
                    subtitle: file.date.toLocaleDateString(),
                    path: getWorkspaceFileURL(file.slug),
                    isLoading: true,
                  })),
                  ...files.map((file) => ({
                    title: file.name,
                    subtitle: file.date.toLocaleDateString(),
                    path: getWorkspaceFileURL(file.slug),
                    badge:
                      file.source === 'sharepoint' ? 'Sharepoint' : undefined,
                  })),
                ]}
                enableSearch={files.length > 2}
              />
            </aside>
          </section>
        </>
      )}

      {createPortal(
        <Modal
          title="Upload File"
          isOpen={showUploadModal}
          onClose={() => {
            if (processingDoc) {
              return;
            }
            setShowUploadModal(false);
          }}
        >
          <Form onSubmit={handleUploadSubmit}>
            <InputWrapper>
              <input
                type="file"
                id="file"
                name="file"
                required
                accept="application/pdf,text/plain,text/markdown,application/markdown,application/xhtml,text/html,text/csv,application/csv,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.presentationml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/json"
              />
              <label htmlFor="file">File</label>
            </InputWrapper>
            <Button
              type="submit"
              loading={processingDoc}
              loadingText="Processing File"
            >
              Upload
            </Button>
          </Form>
        </Modal>,
        document.body
      )}
    </div>
  );
};

export default Library;
